import { useCallback, useMemo } from "react";

/**
 * useValidation Hook
 * Custom hook for form validation logic
 * Optimized with useMemo to prevent recreation of validation rules
 * 
 * @param {string} tab - Current tab
 * @param {string} userType - User type
 * @param {string} sh3CodeForTerritory - SH3 code
 * @param {object} fileValidation - File validation errors
 * @returns {function} Validation function
 */
export const useValidation = (tab, userType, sh3CodeForTerritory, fileValidation) => {
  // Memoize user type checks
  const isHOorZSM = useMemo(() => 
    ["SH6", "SH5", "SH7", "HO Collaborator", "HO Auditor", "MH3", "RBDM Collaborator"].includes(userType),
    [userType]
  );

  const isRMDM = useMemo(() => 
    userType === "MH3" || userType === "RBDM Collaborator",
    [userType]
  );

  const checkValidation = useCallback(
    (values) => {
      const errors = {};

      // Cluster code validation for specific user types
      if ((tab === "Dealer" || tab === "Team" || tab === "WSS") && isHOorZSM && !sh3CodeForTerritory) {
        if (!values.clusterCode) {
          errors.clusterCode = "Please select the Cluster";
        }
      }

      // Territory validation (not for RMDM users)
      if ((tab === "Dealer" || tab === "Team") && !isRMDM && !values.territory) {
        errors.territory = "Please select the territory";
      }

      // Dealer/WSS/Product validation (not for Team tab)
      if (tab !== "Team" && !values.dealerId) {
        errors.dealerId = "Please select the field";
      }

      // WSS validation for RMDM users in Dealer tab
      if (tab === "Dealer" && isRMDM && !values.rmdmWss) {
        errors.rmdmWss = "Please select the WSS";
      }

      // Title validation
      if (!values.Title?.trim()) {
        errors.Title = "Please enter the Title";
      }

      // Observation validation
      if (!values.Observation?.trim()) {
        errors.Observation = "Please enter the Observation";
      }

      // File validation
      if (fileValidation?.file) {
        errors.file = fileValidation.file;
      }

      return errors;
    },
    [tab, isHOorZSM, isRMDM, sh3CodeForTerritory, fileValidation]
  );

  return checkValidation;
};
